// CategoryNavScroll.jsx
import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import CategoryItem from '../component/CategoryItem.jsx';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import ArrowRightIconSource from '../../img/ic_chevron_right.svg';
import media, { hover } from '../style/media.js';
import { color } from '../style/variables.js';

const SCROLL_AMOUNT = 200;

export const CategoryNavScroll = ({ categoryIds = [] }) => {
  const scrollRef = useRef(null);
  const [shouldShowLeftArrow, setShouldShowLeftArrow] = useState(false);
  const [shouldShowRightArrow, setShouldShowRightArrow] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth;
    const isAtStart = el.scrollLeft <= 1;
    const isAtEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    setShouldShowLeftArrow(hasOverflow && !isAtStart);
    setShouldShowRightArrow(hasOverflow && !isAtEnd);
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);

  useEffect(() => {
    checkOverflow();
  }, [categoryIds, checkOverflow]);

  const handleScrollLeft = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
  };

  if (!categoryIds?.length) return null;

  return (
    <StyledCategoryNavScroll>
      {shouldShowLeftArrow && (
        <ArrowButton onClick={handleScrollLeft}>
          <ArrowIcon
            $direction="left"
            src={ArrowRightIconSource}
            alt="scroll left"
          />
        </ArrowButton>
      )}
      <NavList ref={scrollRef} onScroll={checkOverflow}>
        {categoryIds.map(id => (
          <CategoryItem key={id} categoryId={id} isLink height={29} />
        ))}
      </NavList>
      {shouldShowRightArrow && (
        <ArrowButton onClick={handleScrollRight}>
          <ArrowIcon
            $direction="right"
            src={ArrowRightIconSource}
            alt="scroll right"
          />
        </ArrowButton>
      )}
    </StyledCategoryNavScroll>
  );
};

CategoryNavScroll.propTypes = {
  categoryIds: PropTypes.array,
};

const StyledCategoryNavScroll = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin: 8px 0;
`;

const NavList = styled.nav`
  display: flex;
  gap: 8px;
  padding: 8px 0;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  flex-wrap: nowrap;
  &::-webkit-scrollbar {
    display: none;
  }
  & > * {
    flex-shrink: 0;
  }
`;

const ArrowButton = styled.button`
  ${ResetButtonStyle}
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-left: 6px;
  background-color: ${color.grey[900]};
  ${hover`
    opacity: 0.5;
  `}
  ${media.mobile`
    display:none;
  `}
`;

const ArrowIcon = styled.img`
  ${({ $direction }) =>
    $direction === 'left' ? 'transform: rotate(180deg);' : ''}
`;

export default CategoryNavScroll;
